// Vendor Program v2 — create / edit a loan goal. Edit hydrates EVERY field
// and always sends all of them (FIND-12 discipline; "" = clear on nullable
// fields; open-edit-save-unchanged is a server-side no-op). Editing an
// ACTIVE goal requires a one-line reason — the borrower may already have
// seen the text. Citations are provenance and are never editable here.

import { useState } from "react";
import { Button, Input, Label, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { VendorTypeOptGroups } from "@/components/portfolioAdmin/vendors/VendorsListFilters";
import type { LoanGoalDto, LoanGoalPriority } from "@/api/portfolioAdmin/loanGoalsApi";
import {
  useCreateLoanGoal,
  useUpdateLoanGoal,
} from "@/hooks/portfolioAdmin/useLoanGoals";

interface GoalFormModalProps {
  loanId: string;
  /** Null = create. */
  goal: LoanGoalDto | null;
  open: boolean;
  onClose: () => void;
}

const PRIORITY_OPTIONS: { value: LoanGoalPriority; label: string }[] = [
  { value: "high", label: "High" },
  { value: "standard", label: "Standard" },
  { value: "low", label: "Low" },
];

export function GoalFormModal({ loanId, goal, open, onClose }: GoalFormModalProps) {
  const toast = useToast();
  const create = useCreateLoanGoal(loanId);
  const update = useUpdateLoanGoal(loanId);
  const [title, setTitle] = useState(goal?.title ?? "");
  const [description, setDescription] = useState(goal?.description ?? "");
  const [suggestedType, setSuggestedType] = useState(goal?.suggestedVendorType ?? "");
  const [completionCriteria, setCompletionCriteria] = useState(goal?.completionCriteria ?? "");
  const [targetMonths, setTargetMonths] = useState(goal?.targetMonths ?? "");
  const [priority, setPriority] = useState<LoanGoalPriority>(goal?.priority ?? "standard");
  const [reason, setReason] = useState("");
  const requiresReason = goal?.status === "active";
  const pending = create.isPending || update.isPending;

  const onSubmit = async () => {
    if (!title.trim()) {
      toast.error("Goal title is required.");
      return;
    }
    if (!description.trim()) {
      toast.error("Goal description is required.");
      return;
    }
    try {
      if (goal) {
        await update.mutateAsync({
          goalId: goal.id,
          body: {
            title: title.trim(),
            description: description.trim(),
            suggestedVendorType: suggestedType,
            completionCriteria,
            targetMonths: targetMonths.trim(),
            priority,
            reason: reason.trim() || null,
          },
        });
        toast.success("Goal updated.");
      } else {
        await create.mutateAsync({
          title: title.trim(),
          description: description.trim(),
          suggestedVendorType: suggestedType || null,
        });
        toast.success("Goal created.");
      }
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not save the goal.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={goal ? "Edit goal" : "Add goal"}
      size="md"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void onSubmit()}
            disabled={pending}
            data-testid="goal-form-save"
          >
            {pending ? "Saving…" : goal ? "Save changes" : "Create goal"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-3">
        <div>
          <Label htmlFor="goal-title">Title</Label>
          <Input
            id="goal-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="e.g. Move from shoebox records to monthly closes"
          />
        </div>
        <div>
          <Label htmlFor="goal-description">Description</Label>
          <Textarea
            id="goal-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={4000}
            placeholder="What the borrower should work toward, and how progress will be judged."
          />
        </div>
        {goal && (
          <>
            <div>
              <Label htmlFor="goal-completion-criteria">Done when (completion criteria)</Label>
              <Textarea
                id="goal-completion-criteria"
                value={completionCriteria}
                onChange={(e) => setCompletionCriteria(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="The measurable definition of done — what makes the vendor's progress grade meaningful."
                data-testid="goal-form-completion-criteria"
              />
            </div>
            <div className="flex gap-space-3 flex-wrap">
              <div>
                <Label htmlFor="goal-target-months">Target months</Label>
                <Input
                  id="goal-target-months"
                  value={targetMonths}
                  onChange={(e) => setTargetMonths(e.target.value)}
                  maxLength={20}
                  placeholder="e.g. 1-6"
                  className="max-w-[120px]"
                  data-testid="goal-form-target-months"
                />
              </div>
              <div>
                <Label htmlFor="goal-priority">Priority</Label>
                <select
                  id="goal-priority"
                  className="block rounded-input border border-border-light bg-white-pure px-space-2 py-space-1 text-small"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as LoanGoalPriority)}
                  data-testid="goal-form-priority"
                >
                  {PRIORITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
        <div>
          <Label htmlFor="goal-suggested-type">Suggested vendor type (optional)</Label>
          <select
            id="goal-suggested-type"
            className="w-full rounded-input border border-border-light bg-white-pure px-space-2 py-space-1 text-small"
            value={suggestedType}
            onChange={(e) => setSuggestedType(e.target.value)}
            data-testid="goal-form-suggested-type"
          >
            <option value="">Not set</option>
            <VendorTypeOptGroups />
          </select>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            Groups the goal on this tab — advisory only; you can assign the
            goal to any vendor.
          </p>
        </div>
        {requiresReason && (
          <div>
            <Label htmlFor="goal-edit-reason">Reason for change</Label>
            <Input
              id="goal-edit-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={300}
              placeholder="Required — this goal is published to the borrower."
              data-testid="goal-form-reason"
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
